import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon,
  LockClosedIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
  DocumentIcon,
  CreditCardIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

// Updated templates with icons and preview content
const emailTemplates = [
  { 
    id: 1, 
    name: 'Password Reset', 
    description: 'Email claiming to be from IT requesting an urgent password change',
    difficulty: 'Easy',
    icon: LockClosedIcon,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-100',
    category: 'IT',
    successRate: '38%',
    previewContent: {
      subject: 'Urgent: Your Password Will Expire Soon',
      sender: 'IT Security Team <security@company.com>',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0;">
          <div style="text-align: center; margin-bottom: 20px;">
            <img src="https://via.placeholder.com/150x50?text=CompanyLogo" alt="Company Logo" style="max-width: 150px;">
          </div>
          <h2 style="color: #d63031; margin-bottom: 15px;">URGENT: Password Expiration Notice</h2>
          <p>Dear Employee,</p>
          <p>Our records indicate that your company account password will expire in 24 hours. To avoid any disruption to your work, please update your password immediately by clicking the link below:</p>
          <div style="text-align: center; margin: 25px 0;">
            <a href="#" style="display: inline-block; background-color: #3498db; color: white; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-weight: bold;">Reset Password Now</a>
          </div>
          <p>If you do not update your password before it expires, you may be locked out of your account and will need to contact the IT Help Desk.</p>
          <p>Thank you for your prompt attention to this matter.</p>
          <p>Best regards,<br>IT Security Team</p>
          <div style="margin-top: 30px; padding-top: 15px; border-top: 1px solid #e0e0e0; font-size: 12px; color: #7f8c8d;">
            <p>This is an automated message. Please do not reply to this email.</p>
          </div>
        </div>
      `
    }
  },
  { 
    id: 2, 
    name: 'CEO Request', 
    description: 'Urgent request appearing to come from the company CEO',
    difficulty: 'Hard',
    icon: BriefcaseIcon,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-100',
    category: 'Executive',
    successRate: '64%',
    previewContent: {
      subject: 'Urgent Assistance Required - Confidential',
      sender: 'John Smith, CEO <ceo@company.com>',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <p>From: John Smith, CEO</p>
          <p>Sent from my iPhone</p>
          <p>Hi there,</p>
          <p>I'm in a confidential meeting and need your urgent assistance with a task that cannot wait. Are you available right now?</p>
          <p>I need you to purchase some gift cards for our clients as part of our customer appreciation initiative. This is urgent and needs to be done ASAP.</p>
          <p>Let me know if you can help and I'll send the details.</p>
          <p>Thanks,<br>John Smith<br>Chief Executive Officer</p>
        </div>
      `
    }
  },
  { 
    id: 3, 
    name: 'Bonus Notification', 
    description: 'Email notifying employees of a surprise bonus with a login link',
    difficulty: 'Medium',
    icon: CurrencyDollarIcon,
    iconColor: 'text-green-500',
    iconBg: 'bg-green-100',
    category: 'HR',
    successRate: '51%',
    previewContent: {
      subject: 'Your Performance Bonus Information',
      sender: 'Human Resources <hr@company.com>',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0;">
          <h2 style="color: #2ecc71;">Performance Bonus Notification</h2>
          <p>Dear Valued Employee,</p>
          <p>We're pleased to inform you that based on your outstanding performance and our company's strong Q2 results, you've been awarded a performance bonus!</p>
          <p>To view your bonus amount and confirm your payment details, please login to the employee portal by clicking the button below:</p>
          <div style="text-align: center; margin: 25px 0;">
            <a href="#" style="display: inline-block; background-color: #2ecc71; color: white; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-weight: bold;">View Your Bonus</a>
          </div>
          <p>Please note that all bonuses will be processed with the next payroll cycle after you've confirmed your details.</p>
          <p>Congratulations, and thank you for your continued dedication and hard work!</p>
          <p>Best regards,<br>Human Resources Team</p>
        </div>
      `
    }
  },
  { 
    id: 4, 
    name: 'Document Share', 
    description: 'Notification of a shared document requiring login to view',
    difficulty: 'Medium',
    icon: DocumentIcon,
    iconColor: 'text-indigo-500',
    iconBg: 'bg-indigo-100',
    category: 'Office',
    successRate: '47%',
    previewContent: {
      subject: 'Important Document Shared With You',
      sender: 'Document Cloud <notifications@docshare.com>',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0;">
          <div style="text-align: center; margin-bottom: 20px;">
            <img src="https://via.placeholder.com/150x50?text=DocShare" alt="DocShare Logo" style="max-width: 150px;">
          </div>
          <div style="background-color: #f5f6fa; padding: 20px; border-radius: 5px; margin-bottom: 20px;">
            <h2 style="margin-top: 0; color: #3742fa;">A document has been shared with you</h2>
            <p><strong>From:</strong> finance@company.com</p>
            <p><strong>Document:</strong> Quarterly_Budget_Review.xlsx</p>
            <p><strong>Message:</strong> Please review the updated Q3 budget allocation for your department.</p>
          </div>
          <div style="text-align: center; margin: 25px 0;">
            <a href="#" style="display: inline-block; background-color: #3742fa; color: white; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-weight: bold;">View Document</a>
          </div>
          <p style="font-size: 12px; color: #7f8c8d; text-align: center;">This link will expire in 7 days. You may need to login to your DocShare account to access this file.</p>
        </div>
      `
    }
  },
  { 
    id: 5, 
    name: 'Invoice Payment', 
    description: 'Request to review and pay an attached invoice',
    difficulty: 'Medium',
    icon: CreditCardIcon,
    iconColor: 'text-yellow-500',
    iconBg: 'bg-yellow-100',
    category: 'Finance',
    successRate: '43%',
    previewContent: {
      subject: 'Invoice #INV-2023-4872 Due for Payment',
      sender: 'Accounting Department <accounting@supplier-name.com>',
      body: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0;">
          <h2 style="color: #2c3e50;">Invoice Payment Reminder</h2>
          <p>Dear Customer,</p>
          <p>This is a friendly reminder that invoice <strong>#INV-2023-4872</strong> for $3,842.75 is due for payment on September 15, 2023.</p>
          <div style="background-color: #f8f9fa; padding: 15px; margin: 20px 0; border-left: 4px solid #2c3e50;">
            <p><strong>Invoice Details:</strong></p>
            <p>Invoice Number: INV-2023-4872<br>
            Amount Due: $3,842.75<br>
            Due Date: September 15, 2023</p>
          </div>
          <p>To view and pay your invoice, please click the button below:</p>
          <div style="text-align: center; margin: 25px 0;">
            <a href="#" style="display: inline-block; background-color: #e67e22; color: white; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-weight: bold;">View and Pay Invoice</a>
          </div>
          <p>If you've already made this payment, please disregard this notice.</p>
          <p>Thank you for your prompt attention to this matter.</p>
          <p>Best regards,<br>Accounting Department</p>
        </div>
      `
    }
  },
];

const userGroups = [
  { 
    id: 1, 
    name: 'All Employees', 
    count: 253,
    description: 'Everyone in the organization' 
  },
  { 
    id: 2, 
    name: 'Executive Team', 
    count: 8,
    description: 'C-level executives and VPs' 
  },
  { 
    id: 3, 
    name: 'Finance Department', 
    count: 18,
    description: 'Finance and accounting staff' 
  },
  { 
    id: 4, 
    name: 'IT Department', 
    count: 32,
    description: 'IT staff including helpdesk and development' 
  },
  { 
    id: 5, 
    name: 'Marketing', 
    count: 24,
    description: 'Marketing and communications' 
  },
  { 
    id: 6, 
    name: 'Sales', 
    count: 46,
    description: 'Sales representatives and managers' 
  },
  { 
    id: 7, 
    name: 'Human Resources', 
    count: 12,
    description: 'HR personnel' 
  },
  { 
    id: 8, 
    name: 'New Employees', 
    count: 15,
    description: 'Employees hired in the last 90 days' 
  },
];

const formatDate = (date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

const CampaignWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))); // One week from now
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [sendTime, setSendTime] = useState('09:00');
  const [staggerDelivery, setStaggerDelivery] = useState(true);
  const [maxStaggerHours, setMaxStaggerHours] = useState(8);
  const [includeReportButton, setIncludeReportButton] = useState(true);
  const [trackOpens, setTrackOpens] = useState(true);
  const [trackClicks, setTrackClicks] = useState(true);
  const [sendingName, setSendingName] = useState('IT Security Team');
  const [sendingEmail, setSendingEmail] = useState('security@company.com');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  // Calculate total target recipients
  const totalRecipients = selectedGroups.reduce((sum, groupId) => {
    const group = userGroups.find(g => g.id === groupId);
    return sum + (group ? group.count : 0);
  }, 0);
  
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!campaignName.trim()) newErrors.campaignName = 'Campaign name is required';
      if (new Date(startDate) > new Date(endDate)) newErrors.dates = 'End date must be after start date';
    }
    
    if (step === 2) {
      if (!selectedTemplate) newErrors.template = 'Please select an email template';
    }
    
    if (step === 3) {
      if (selectedGroups.length === 0) newErrors.groups = 'Please select at least one target group';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const goToNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  
  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };
  
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
  };
  
  const handleGroupToggle = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId) 
        : [...prev, groupId]
    );
  };
  
  const handleSubmit = async () => {
    if (validateStep(currentStep)) {
      setIsSubmitting(true);
      
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setSuccess(true);
        setTimeout(() => {
          navigate('/admin/campaigns');
        }, 2000);
      } catch (error) {
        console.error('Error creating campaign:', error);
        setErrors({ submit: 'Failed to create campaign. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  const openTemplatePreview = (templateId, e) => {
    e.stopPropagation(); // Prevent selecting the template when clicking preview
    const template = emailTemplates.find(t => t.id === templateId);
    setPreviewTemplate(template);
    setShowTemplatePreview(true);
  };
  
  const closeTemplatePreview = () => {
    setShowTemplatePreview(false);
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      {success ? (
        <div className="text-center py-12">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Campaign Created Successfully!</h2>
          <p className="mt-2 text-gray-600">Your campaign has been scheduled. Redirecting to campaign management...</p>
        </div>
      ) : (
        <>
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Create Phishing Simulation Campaign</h1>
            <p className="mt-2 text-gray-600">Set up a new phishing simulation campaign to test and train employees.</p>
          </div>
          
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {[1, 2, 3, 4].map((step) => (
                <div 
                  key={step} 
                  className={`flex-1 ${
                    step < currentStep 
                      ? 'text-primary-600' 
                      : step === currentStep 
                        ? 'text-primary-800 font-semibold' 
                        : 'text-gray-400'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full ${
                      step < currentStep 
                        ? 'bg-primary-500' 
                        : step === currentStep 
                          ? 'bg-primary-700' 
                          : 'bg-gray-200'
                      } flex items-center justify-center text-white`}>
                      {step}
                    </div>
                    <div className={`ml-2 text-sm hidden md:block ${
                      step === currentStep ? 'font-medium' : ''
                    }`}>
                      {step === 1 && 'Campaign Details'}
                      {step === 2 && 'Email Template'}
                      {step === 3 && 'Target Audience'}
                      {step === 4 && 'Review & Launch'}
                    </div>
                    
                    {step < 4 && (
                      <div className={`flex-1 h-0.5 mx-2 ${
                        step < currentStep ? 'bg-primary-500' : 'bg-gray-200'
                      }`}></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Step 1: Campaign Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                
                <div className="form-group">
                  <label htmlFor="campaignName" className="form-label">
                    Campaign Name*
                  </label>
                  <input
                    type="text"
                    id="campaignName"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    className={`input-field ${errors.campaignName ? 'border-red-500' : ''}`}
                    placeholder="Enter campaign name"
                  />
                  {errors.campaignName && (
                    <p className="mt-1 text-sm text-red-600">{errors.campaignName}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="campaignDescription" className="form-label">
                    Description
                  </label>
                  <textarea
                    id="campaignDescription"
                    value={campaignDescription}
                    onChange={(e) => setCampaignDescription(e.target.value)}
                    rows="3"
                    className="input-field"
                    placeholder="Brief description of the campaign purpose"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="startDate" className="form-label">
                      Start Date*
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      min={formatDate(new Date())}
                      onChange={(e) => setStartDate(e.target.value)}
                      className={`input-field ${errors.dates ? 'border-red-500' : ''}`}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="endDate" className="form-label">
                      End Date*
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      value={endDate}
                      min={startDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={`input-field ${errors.dates ? 'border-red-500' : ''}`}
                    />
                  </div>
                </div>
                
                {errors.dates && (
                  <p className="mt-1 text-sm text-red-600">{errors.dates}</p>
                )}
              </div>
            </div>
          )}
          
          {/* Step 2: Email Template */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Select Email Template</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Choose the email template that will be sent to your target audience.
                </p>
                
                {errors.template && (
                  <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                    {errors.template}
                  </div>
                )}
                
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {emailTemplates.map((template) => (
                    <div 
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id)}
                      className={`border rounded-lg p-4 cursor-pointer hover:border-primary-500 transition-colors
                        ${selectedTemplate === template.id 
                          ? 'border-primary-500 ring-2 ring-primary-200 bg-primary-50' 
                          : 'border-gray-200'
                        }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3 rounded-lg ${template.iconBg}`}>
                          <template.icon className={`w-8 h-8 ${template.iconColor}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center">
                            <h3 className="font-medium text-gray-900">{template.name}</h3>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={(e) => openTemplatePreview(template.id, e)}
                                className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full text-primary-700 bg-primary-100 hover:bg-primary-200"
                              >
                                <EyeIcon className="w-3.5 h-3.5 mr-1" />
                                Preview
                              </button>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                template.difficulty === 'Easy' 
                                  ? 'bg-green-100 text-green-800' 
                                  : template.difficulty === 'Medium'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                {template.difficulty}
                              </span>
                            </div>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                          <div className="mt-2 flex items-center text-xs text-gray-500">
                            <span className="mr-3">Category: {template.category}</span>
                            <span>Success rate: {template.successRate}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: Target Audience */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="card mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Target Recipients</h3>
                
                <div className="form-group">
                  <label className="form-label">Select User Groups</label>
                  {errors.groups && (
                    <p className="mt-1 mb-2 text-sm text-red-600">{errors.groups}</p>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    {userGroups.map((group) => (
                      <div
                        key={group.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          selectedGroups.includes(group.id)
                            ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                            : 'border-gray-200 hover:border-primary-400'
                        }`}
                        onClick={() => handleGroupToggle(group.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900">{group.name}</h4>
                            <p className="text-sm text-gray-500 mt-1">{group.description}</p>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {group.count} users
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 bg-blue-50 p-4 rounded-md flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-blue-700">
                      Selected: <span className="font-medium">{totalRecipients}</span> total recipients
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="card mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Settings</h3>
                
                <div className="form-group">
                  <label htmlFor="sendTime" className="form-label">Delivery Time</label>
                  <input
                    type="time"
                    id="sendTime"
                    value={sendTime}
                    onChange={(e) => setSendTime(e.target.value)}
                    className="input-field max-w-xs"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    The time when emails will begin to be sent (in your local timezone).
                  </p>
                </div>
                
                <div className="form-group">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="staggerDelivery"
                        type="checkbox"
                        checked={staggerDelivery}
                        onChange={(e) => setStaggerDelivery(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="staggerDelivery" className="form-label !mb-0">
                        Stagger Email Delivery
                      </label>
                      <p className="text-sm text-gray-500">
                        Distribute email sending over time to avoid detection and make the campaign more realistic.
                      </p>
                    </div>
                  </div>
                </div>
                
                {staggerDelivery && (
                  <div className="form-group ml-7">
                    <label htmlFor="maxStaggerHours" className="form-label">
                      Maximum Hours to Stagger
                    </label>
                    <div className="flex items-center">
                      <input
                        type="range"
                        id="maxStaggerHours"
                        min="1"
                        max="24"
                        value={maxStaggerHours}
                        onChange={(e) => setMaxStaggerHours(parseInt(e.target.value))}
                        className="mr-3 w-48"
                      />
                      <span className="text-gray-700">{maxStaggerHours} hours</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Emails will be randomly distributed within {maxStaggerHours} hours after the start time.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Email Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="form-group">
                    <label htmlFor="sendingName" className="form-label">
                      Sender Name
                    </label>
                    <input
                      type="text"
                      id="sendingName"
                      value={sendingName}
                      onChange={(e) => setSendingName(e.target.value)}
                      className="input-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="sendingEmail" className="form-label">
                      Sender Email
                    </label>
                    <input
                      type="email"
                      id="sendingEmail"
                      value={sendingEmail}
                      onChange={(e) => setSendingEmail(e.target.value)}
                      className="input-field"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This is the email address that will appear in the From field.
                    </p>
                  </div>
                </div>
                
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <h4 className="text-base font-medium text-gray-900 mb-3">
                    Tracking Options
                  </h4>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="includeReportButton"
                          type="checkbox"
                          checked={includeReportButton}
                          onChange={(e) => setIncludeReportButton(e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3">
                        <label htmlFor="includeReportButton" className="form-label !mb-0">
                          Include Report Phishing Button
                        </label>
                        <p className="text-sm text-gray-500">
                          Add a button in emails that allows users to report the phishing attempt.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="trackOpens"
                          type="checkbox"
                          checked={trackOpens}
                          onChange={(e) => setTrackOpens(e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3">
                        <label htmlFor="trackOpens" className="form-label !mb-0">
                          Track Email Opens
                        </label>
                        <p className="text-sm text-gray-500">
                          Track when recipients open the email.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="trackClicks"
                          type="checkbox"
                          checked={trackClicks}
                          onChange={(e) => setTrackClicks(e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3">
                        <label htmlFor="trackClicks" className="form-label !mb-0">
                          Track Link Clicks
                        </label>
                        <p className="text-sm text-gray-500">
                          Track when recipients click on links in the email.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 4: Review & Launch */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Review Campaign</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Review your campaign settings before launching.
                </p>
                
                <div className="mt-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-4 py-5 sm:px-6 bg-gray-50">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Campaign Details
                    </h3>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Campaign Name</dt>
                        <dd className="mt-1 text-sm text-gray-900">{campaignName}</dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Description</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {campaignDescription || "No description provided"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                        <dd className="mt-1 text-sm text-gray-900">{startDate}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">End Date</dt>
                        <dd className="mt-1 text-sm text-gray-900">{endDate}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Email Template</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedTemplate ? emailTemplates.find(t => t.id === selectedTemplate)?.name : 'None selected'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Target Recipients</dt>
                        <dd className="mt-1 text-sm text-gray-900">{totalRecipients} users</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Selected Groups</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedGroups.length > 0 
                            ? userGroups
                                .filter(g => selectedGroups.includes(g.id))
                                .map(g => g.name)
                                .join(', ')
                            : 'None selected'
                          }
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Delivery Time</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {sendTime} {staggerDelivery ? `(staggered over ${maxStaggerHours} hours)` : ''}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Sender Information</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {sendingName} &lt;{sendingEmail}&gt;
                        </dd>
                      </div>
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Tracking Options</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          <ul className="list-disc pl-5">
                            {includeReportButton && (
                              <li>Report Phishing Button Included</li>
                            )}
                            {trackOpens && (
                              <li>Email Opens Tracking Enabled</li>
                            )}
                            {trackClicks && (
                              <li>Link Clicks Tracking Enabled</li>
                            )}
                          </ul>
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
                
                {errors.submit && (
                  <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md flex items-start">
                    <ExclamationCircleIcon className="h-5 w-5 mr-2 text-red-500" />
                    <span>{errors.submit}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="mt-8 pt-5 border-t border-gray-200 flex justify-between">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={goToPreviousStep}
                className="btn btn-outline"
              >
                Previous
              </button>
            ) : (
              <div></div>
            )}
            
            {currentStep < 4 ? (
              <button
                type="button"
                onClick={goToNextStep}
                className="btn btn-primary"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn btn-primary"
              >
                {isSubmitting ? 'Creating Campaign...' : 'Launch Campaign'}
              </button>
            )}
          </div>
        </>
      )}
      
      <Transition appear show={showTemplatePreview} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeTemplatePreview}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="div"
                    className="flex justify-between items-center border-b pb-3 mb-4"
                  >
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      Email Preview: {previewTemplate?.name}
                    </h3>
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500"
                      onClick={closeTemplatePreview}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </Dialog.Title>
                  
                  {previewTemplate && (
                    <div className="mt-2">
                      <div className="border rounded-md p-4 bg-gray-50 mb-4">
                        <p><strong>Subject:</strong> {previewTemplate.previewContent.subject}</p>
                        <p><strong>From:</strong> {previewTemplate.previewContent.sender}</p>
                      </div>
                      
                      <div className="border rounded-md p-4 bg-white">
                        <div dangerouslySetInnerHTML={{ __html: previewTemplate.previewContent.body }} />
                      </div>
                      
                      <div className="mt-4 p-3 bg-yellow-50 text-yellow-800 rounded-md flex items-start">
                        <ExclamationCircleIcon className="h-5 w-5 mr-2 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">
                          This is how the email will appear to recipients. The actual sent email will include tracking capabilities and your custom sender information.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={closeTemplatePreview}
                    >
                      Close Preview
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default CampaignWizard; 